import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authBypassActive = process.env.REACT_APP_DEV_MODE_BYPASS_AUTH === 'true';
  const navigate = useNavigate();

  const bypassUser = useMemo(
    () =>
      authBypassActive
        ? {
            email: 'devmode@example.com',
            name: 'Dev Mode Administrator',
            groups: ['admin'],
            is_admin: true,
            authenticated: true,
          }
        : null,
    [authBypassActive]
  );

  useEffect(() => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }

    api.get('/api/auth/me')
      .then(res => {
        setUser(res.data);
        setLoading(false);
      })
      .catch(() => {
         setLoading(false);
      });
  }, [authBypassActive, bypassUser]);

  useEffect(() => {
    if (!authBypassActive && !loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authBypassActive, loading, user, navigate]);

  const login = async () => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }
    // Redirect browser to OIDC login (handled server-side)
    window.location.href = '/api/auth/login';
  };

  const logout = async () => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }
    await api.post('/api/auth/logout');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: Boolean(user),
    authBypassActive,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
